import { ADMIN_ROLE } from '@carsfinders/auth';
import { useQueries } from '@tanstack/react-query';

import { dealsQueueQueryKey, fetchDealsQueue } from '../features/deals/api';
import { fetchDealerApplications } from '../features/dealer/api';
import { fetchDocuments } from '../features/documents/api';
import {
  FINANCE_CLIENTS_QUERY_KEY,
  adminTopUpRequestsQueryKey,
  fetchFinanceClients,
  fetchTopUpRequests,
} from '../features/finance/api';
import { isSuperadmin } from '../features/auth/use-session-roles';
import { fetchAdminListings } from '../features/marketplace/api';
import { type NavItem } from './nav-items';

/**
 * The counts the artboard draws as pills on the sidebar and the mobile bar.
 *
 * The artboard's numbers are mock; these are the live queues. Query keys are the
 * same ones the dashboard tiles use, so the shell and the Overview screen share
 * one fetch rather than racing each other, and a mutation on a feature page
 * updates the badge without this hook knowing that page exists.
 *
 * A queue that fails to load renders no badge at all. A wrong number on the
 * navigation is worse than no number: it sends an operator to an empty screen.
 */
export type NavBadges = Partial<Record<NonNullable<NavItem['badgeKey']>, number>>;

export function useNavBadges(roles: string[] = [ADMIN_ROLE]): NavBadges {
  // The moderation queue is a superuser screen (2026-10-13): a plain admin's
  // request would only earn a 403, so it is never sent.
  const canModerate = isSuperadmin(roles);
  const [documents, deals, dealers, marketplace, finance, topUps] = useQueries({
    queries: [
      { queryKey: ['admin-documents', 'pending'], queryFn: () => fetchDocuments('pending') },
      /*
       * Under the queue's key PREFIX, so the bid stream's invalidation of
       * «Угоди» refreshes the pill too. Not the screen's own key: that one is
       * an infinite query (pages), this is one page of ONE row — the counters
       * cover the whole source set whatever the page size.
       */
      {
        queryKey: [...dealsQueueQueryKey('all', ''), 'badge'],
        queryFn: () => fetchDealsQueue({ tab: 'all', limit: 1 }),
      },
      {
        queryKey: ['dealer-applications', 'pending'],
        queryFn: () => fetchDealerApplications('pending'),
      },
      {
        queryKey: ['admin-marketplace-listings', 'moderation'],
        queryFn: () => fetchAdminListings({ status: 'moderation', limit: 1 }),
        enabled: canModerate,
      },
      // The SAME query key «Фінанси» itself uses, so opening the screen costs
      // no second round trip and a refetch there updates the pill here.
      { queryKey: FINANCE_CLIENTS_QUERY_KEY, queryFn: fetchFinanceClients },
      // The SAME query key the top-up requests desk uses (phase 6) — a
      // confirm/reject there invalidates this and the pill updates for free.
      { queryKey: adminTopUpRequestsQueryKey('open'), queryFn: () => fetchTopUpRequests('open') },
    ],
  });

  const badges: NavBadges = {};
  if (documents?.data) badges.documents = documents.data.pagination.total;
  /**
   * Bids WAITING TO BE MIRRORED, not deals in general: the relay queue is the
   * one thing that loses money if nobody looks (a tender closes on a bid that
   * never reached the auction), so it is the number the pill is for — the
   * artboard's «Угоди 4» is the relay counter.
   */
  if (deals?.data) badges.deals = deals.data.counts.relay ?? 0;
  if (dealers?.data) badges.dealers = dealers.data.applications.length;
  if (canModerate && marketplace?.data) badges.marketplace = marketplace.data.total;
  /**
   * CLIENTS past due, PLUS open top-up requests — one pill, two queues that
   * both mean "an operator needs to act on this client's money". Not a
   * separate `badgeKey`: the plan is explicit that «Фінанси» keeps ONE badge
   * rather than growing a second pill next to it.
   *
   * `overdueChf` is the server's own fold over the same invoices the screen
   * renders, so that half cannot drift from the «Прострочено» card. A client
   * whose only late invoice is `checking` («Я сплатив») is NOT counted: the
   * contract lets `checking` outrank the clock, so nobody is chased over it.
   * The top-up half is simply how many requests are still `open`.
   */
  if (finance?.data) {
    const overdue = finance.data.filter((client) => client.overdueChf > 0).length;
    // Additive only: a still-loading or failed top-up queue must not blank
    // the overdue count the «Прострочено» card already committed to showing.
    const openTopUps = topUps?.data?.length ?? 0;
    badges.finance = overdue + openTopUps;
  }
  return badges;
}
